import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Contact Speedy DMV Services Las Vegas | Call 702.979.7202',
    description: 'Contact Speedy DMV Services in Las Vegas Nevada. Call 702.979.7202 or visit 4525 S Sandhill Rd for vehicle registration, title transfer, and all Nevada DMV needs. Appointments required.',
    keywords: 'contact DMV Las Vegas, Speedy DMV phone number, Las Vegas DMV office, DMV services contact Nevada',
    canonical: `${siteUrl}/contact`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Speedy DMV Services",
          "description": "Contact Speedy DMV Services in Las Vegas for all your Nevada DMV needs.",
          "url": `${siteUrl}/contact`
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' }
        ])
      ]
    }
  });

  const services = [
    'Vehicle Registration',
    'Title Transfer',
    'License Plates',
    'Duplicate Title',
    'Temporary Permits',
    'Notary Services',
    'General Inquiry'
  ];

  const contactInfo = [
    {
      icon: 'fas fa-phone',
      title: 'Phone',
      info: '(702) 979-7202',
      action: 'tel:702-979-7202',
      color: 'bg-teal-100'
    },
    {
      icon: 'fas fa-envelope',
      title: 'Email',
      info: 'info@speedydmvlv.com',
      action: 'mailto:info@speedydmvlv.com',
      color: 'bg-teal-100'
    },
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Address',
      info: '4525 S Sandhill Rd, Las Vegas, NV',
      action: 'https://maps.google.com/?q=4525+S+Sandhill+Rd+Las+Vegas+NV',
      color: 'bg-teal-100'
    },
    {
      icon: 'fas fa-clock',
      title: 'Hours',
      info: 'Mon-Fri: 9AM-5PM, Sat: 10AM-3PM',
      action: '',
      color: 'bg-teal-100'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Contact Us Today
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Ready to get started? Contact us for fast, professional DMV services
            </p>
          </div>
        </div>
      </section>

      {/* ⛔ STRICT NO SOLICITATION POLICY */}
      <section className="bg-red-950 border-b-4 border-red-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1">
              <i className="ri-error-warning-fill text-red-400 text-4xl"></i>
            </div>
            <div>
              <h2 className="text-2xl lg:text-3xl font-black text-white uppercase tracking-widest mb-1">
                ⛔ Strict No Solicitation Policy
              </h2>
              <p className="text-red-300 text-sm font-semibold uppercase tracking-wider">
                Read carefully before submitting any message or inquiry
              </p>
            </div>
          </div>

          <div className="bg-red-900/60 border border-red-700 rounded-lg p-6 mb-6">
            <p className="text-red-100 text-base font-semibold leading-relaxed mb-4">
              This website and all associated businesses <strong className="text-white">strictly prohibit</strong> any unsolicited communications, including but not limited to sales, marketing, lead generation, promotional offers, partnerships, or outreach of any kind.
            </p>
            <p className="text-red-200 text-sm leading-relaxed mb-5">
              This contact form and all communication channels are <strong className="text-white uppercase">exclusively reserved</strong> for:
            </p>
            <ul className="space-y-2 mb-5">
              {[
                'Clients and patients seeking services',
                'Authorized referral sources',
                'Verified business matters directly related to existing operations'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-red-100 text-sm font-medium">
                  <i className="ri-checkbox-circle-fill text-red-400 mt-0.5 flex-shrink-0"></i>
                  {item}
                </li>
              ))}
            </ul>
            <div className="border-t border-red-700 pt-5">
              <p className="text-red-200 text-sm font-bold uppercase tracking-wide mb-3">
                By submitting any form, message, or communication, you expressly acknowledge and agree that:
              </p>
              <ul className="space-y-2">
                {[
                  'Unsolicited sales or marketing messages are strictly prohibited',
                  'Your information may be automatically filtered, blocked, or reported as spam',
                  'Repeated violations may result in reporting to relevant regulatory authorities, blacklisting, and legal action if applicable'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-red-100 text-sm">
                    <i className="ri-close-circle-fill text-red-500 mt-0.5 flex-shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-black/40 border border-red-800 rounded-lg px-6 py-4">
            <p className="text-red-300 text-sm font-bold text-center uppercase tracking-widest">
              We do not engage with cold outreach under any circumstances. &nbsp;|&nbsp; Any submission not meeting the above criteria will be ignored, filtered, and permanently blocked without response. &nbsp;|&nbsp; Continued disregard of this policy constitutes intentional interference with business operations.
            </p>
          </div>

          <p className="text-center text-red-400 text-xs font-semibold mt-4 uppercase tracking-widest">
            Proceed only if your inquiry is legitimate and relevant.
          </p>
        </div>
      </section>

      {/* 📋 Careers Policy Notice */}
      <section className="bg-amber-950 border-b-4 border-amber-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1">
              <i className="ri-briefcase-4-fill text-amber-400 text-3xl"></i>
            </div>
            <div>
              <h2 className="text-xl lg:text-2xl font-black text-white uppercase tracking-widest mb-1">
                Careers &amp; Employment Applications — Important Notice
              </h2>
              <p className="text-amber-300 text-sm font-semibold uppercase tracking-wider">
                All applicants must read and comply with the following policy
              </p>
            </div>
          </div>

          <div className="bg-amber-900/50 border border-amber-700 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-white font-black text-base uppercase tracking-wide mb-3 flex items-center gap-2">
                  <i className="ri-phone-fill text-amber-400"></i>
                  Do NOT Call to Follow Up
                </h3>
                <p className="text-amber-100 text-sm leading-relaxed">
                  <strong className="text-white">Calling or repeatedly contacting our office to inquire about application status or to follow up on employment opportunities will result in the immediate and permanent cancellation of your application.</strong> No exceptions will be made under any circumstances.
                </p>
              </div>
              <div>
                <h3 className="text-white font-black text-base uppercase tracking-wide mb-3 flex items-center gap-2">
                  <i className="ri-user-search-fill text-amber-400"></i>
                  HR Review Process
                </h3>
                <p className="text-amber-100 text-sm leading-relaxed">
                  Our Human Resources team reviews all applications internally. <strong className="text-white">If you are qualified, HR will contact you directly.</strong> Unsolicited follow-up calls, emails, or walk-ins regarding employment status are considered a violation of our hiring policy and will disqualify your application permanently.
                </p>
              </div>
            </div>

            <div className="mt-5 border-t border-amber-700 pt-5">
              <div className="bg-black/30 border border-amber-800 rounded-lg px-5 py-4">
                <p className="text-amber-200 text-sm font-bold text-center uppercase tracking-wider">
                  <i className="ri-alarm-warning-fill text-amber-400 mr-2"></i>
                  Follow-up calls for employment = Permanent Application Cancellation &nbsp;|&nbsp; HR will reach out if you are a qualified candidate &nbsp;|&nbsp; Do not call. Do not email to follow up. Do not visit in person to inquire.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((item, index) => (
              <div key={index} className="text-center">
                <div className={`w-16 h-16 ${item.color} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${item.icon} text-teal-600 text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                {item.action ? (
                  <a 
                    href={item.action}
                    className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
                  >
                    {item.info}
                  </a>
                ) : (
                  <p className="text-gray-600">{item.info}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Zoho CRM Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
              
              <div id='crmWebToEntityForm' className='zcwf_lblLeft crmWebToEntityForm'>
                <form 
                  id='webform7155412000000630003' 
                  action='https://crm.zoho.com/crm/WebToLeadForm' 
                  name='WebToLeads7155412000000630003' 
                  method='POST' 
                  acceptCharset='UTF-8'
                >
                  <input type='text' style={{ display: 'none' }} name='xnQsjsdp' value='888e4a72d32be66c0a9b89a7dc4e8bb21f37ec81fb5cbf430d09de8fec559fe7' readOnly />
                  <input type='hidden' name='zc_gad' id='zc_gad' value='' />
                  <input type='text' style={{ display: 'none' }} name='xmIwtLD' value='aad99b585fb71589a13576818e6b68f256c6a3fff07200e07f53beddb812aa5cf2c800cd33a427abaa20bbc18dea4bde' readOnly />
                  <input type='text' style={{ display: 'none' }} name='actionType' value='TGVhZHM=' readOnly />
                  <input type='text' style={{ display: 'none' }} name='returnURL' value='https://speedydmvlv.com/thank-you' readOnly />
                  
                  <div className='space-y-6'>
                    <div>
                      <label htmlFor='Last_Name' className="block text-sm font-semibold text-gray-900 mb-2">
                        Full Name <span className="text-red-600">*</span>
                      </label>
                      <input 
                        type='text' 
                        id='Last_Name' 
                        name='Last Name' 
                        maxLength={80} 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-base"
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor='Email' className="block text-sm font-semibold text-gray-900 mb-2">
                        Email Address <span className="text-red-600">*</span>
                      </label>
                      <input 
                        type='email' 
                        id='Email' 
                        name='Email' 
                        ftype='email'
                        maxLength={100} 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-base"
                        placeholder="your.email@example.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor='Phone' className="block text-sm font-semibold text-gray-900 mb-2">
                        Phone Number <span className="text-red-600">*</span>
                      </label>
                      <input 
                        type='tel' 
                        id='Phone' 
                        name='Phone' 
                        maxLength={30} 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-base"
                        placeholder="(702) 123-4567"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor='LEADCF1' className="block text-sm font-semibold text-gray-900 mb-2">
                        Service Needed
                      </label>
                      <select 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-base bg-white" 
                        id='LEADCF1' 
                        name='LEADCF1'
                      >
                        <option value='-None-'>Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor='Description' className="block text-sm font-semibold text-gray-900 mb-2">
                        Message
                      </label>
                      <textarea 
                        id='Description' 
                        name='Description' 
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none text-base"
                        placeholder="Tell us about your DMV needs..."
                        rows={5}
                      ></textarea>
                    </div>
                    
                    <button 
                      type='submit' 
                      className='formsubmit w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-base'
                    >
                      <span className="flex items-center justify-center">
                        <i className="fas fa-paper-plane mr-2"></i>
                        Send Message
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Map and Additional Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Visit Our Office</h2>
              
              {/* Google Map */}
              <div className="mb-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.8948108432234!2d-115.06516!3d36.08398!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c0b5c1234567%3A0x1234567890abcdef!2s4525%20S%20Sandhill%20Rd%2C%20Las%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1234567890123"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg shadow-lg"
                ></iframe>
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <div className="bg-teal-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-teal-900 mb-3">Need Immediate Help?</h3>
                  <p className="text-teal-800 mb-4">Call us now for urgent DMV services or questions.</p>
                  <a 
                    href="tel:702-979-7202"
                    className="inline-flex items-center px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="fas fa-phone mr-2"></i>
                    Call (702) 979-7202
                  </a>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Business Hours</h3>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span className="font-semibold">9:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span className="font-semibold">10:00 AM - 3:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span className="font-semibold">Closed</span>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-orange-900 mb-3">What to Bring</h3>
                  <ul className="space-y-2 text-orange-800">
                    <li className="flex items-center">
                      <i className="fas fa-check mr-2 text-orange-600"></i>
                      Valid ID or Driver's License
                    </li>
                    <li className="flex items-center">
                      <i className="fas fa-check mr-2 text-orange-600"></i>
                      Vehicle Title (if applicable)
                    </li>
                    <li className="flex items-center">
                      <i className="fas fa-check mr-2 text-orange-600"></i>
                      Insurance Documentation
                    </li>
                    <li className="flex items-center">
                      <i className="fas fa-check mr-2 text-orange-600"></i>
                      Payment Method
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                question: "How long does vehicle registration take?",
                answer: "Most vehicle registrations are completed the same day, typically within 1-2 hours."
              },
              {
                question: "What payment methods do you accept?",
                answer: "We accept cash, check, and all major credit cards for your convenience."
              },
              {
                question: "Do I need an appointment?",
                answer: "Yes, appointments are required. Please call 702.979.7202 to schedule."
              },
              {
                question: "Can you handle out-of-state transfers?",
                answer: "Yes, we specialize in out-of-state vehicle transfers to Nevada registration."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
